import { gql } from '@apollo/client'
import React, { Fragment, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AuthTitle from 'components/internal/AuthTitle'
import Card from 'components/cards/Card'
import Column from 'components/base/Column'
import dx from 'lib/dashx'
import OrganizationLoginForm from 'components/forms/OrganizationLoginForm'
import Row from 'components/base/Row'
import Tab from 'components/tabs/Tab'
import Tabs from 'components/tabs/Tabs'
import useClientMutation from 'hooks/useClientMutation'
import useClientQuery from 'hooks/useClientQuery'
import useMutation from 'hooks/useMutation'
import useURLParams from 'hooks/useURLParams'
import { accountType, isExternalRedirectLogin } from 'models/Account'
import { ADD_ACCOUNT_TOKEN, GET_AUTH, LOGIN } from 'client/state/auth'
import { GATagConversionEvent } from 'components/analytics/GATagManager'
import { SEND_MFA_CODE_MUTATION } from 'models/MFA'

export const CREATE_SESSION_MUTATION = gql`
  mutation CreateSessionMutation($input: CreateSessionInput!) {
    createSession(input: $input) {
      id
      accessToken
      dashxToken
      expiresAt
      externalToken
      status
      verificationUrl
      mfaChallenges {
        id
        channel
        targetMasked
        expiresAt
        createdAt
      }

      mfaBackupTarget
      mfaBackupConfirmedAt
      mfaLastBackupSentAt
      mfaEnrolledAt
      mfaBackupChannel
      accountId
    }
  }
`

function OrganizationLoginPage({ location: { state: { account } = {} } }) {
  const { t } = useTranslation()
  const history = useHistory()
  const urlParams = useURLParams()
  const referrer = urlParams.get('referrer')

  const {
    data: { auth: { accountTokens = [], currentToken = '' } = {} } = {}
  } = useClientQuery(GET_AUTH)

  const [addAccountToken] = useClientMutation(ADD_ACCOUNT_TOKEN)
  const [login] = useClientMutation(LOGIN)

  const [session, setSession] = useState(null)
  const [createSession] = useMutation(CREATE_SESSION_MUTATION, {
    context: {
      includeClientType: true
    },
    onCompleted: response => {
      const createdSession = response.createSession

      if (!response.createSession) {
        history.push('/login')
        return
      }

      const { accessToken, accountId, dashxToken } = createdSession

      setSession(createdSession)
      addAccountToken({
        accountId,
        dashxToken,
        token: accessToken
      })
    }
  })

  const [sendMfaCode] = useMutation(SEND_MFA_CODE_MUTATION)

  const pickPrimaryChallenge = (mfaEnrolledAt, mfaBackupChannel) => {
    if (mfaEnrolledAt) return 'totp'

    return mfaBackupChannel
  }

  useEffect(() => {
    if (
      session &&
      accountTokens?.find(token => token.accountId === session.accountId) &&
      currentToken !== session.accessToken
    ) {
      const {
        accountId,
        status,
        dashxToken,
        accessToken,
        externalToken,
        verificationUrl,
        mfaChallenges = [],
        mfaEnrolledAt = '',
        mfaBackupChannel = '',
        mfaBackupTarget = ''
      } = session

      if (verificationUrl !== null) {
        history.push({
          pathname: `/${externalToken}/verify`,
          state: { account, unverifiedSession: session }
        })
        return
      }

      if (status === 'unverified') {
        const primaryChallenge = pickPrimaryChallenge(
          mfaEnrolledAt,
          mfaBackupChannel
        )

        if (primaryChallenge) {
          const channel = primaryChallenge
          if (channel !== 'totp') {
            sendMfaCode({
              channel,
              target: channel === 'sms' ? null : account.email,
              accessToken
            })
          }

          history.push({
            pathname: '/mfa/verify',
            state: {
              session,
              account,
              channel,
              mfaContext: {
                backup_target_masked: mfaBackupTarget,
                channels: mfaChallenges.map(ch => ({
                  type: ch.channel,
                  status: 'pending',
                  label: null,
                  target_masked: ch.targetMasked
                })),
                mfaBackupChannel
              }
            }
          })
          return
        }

        history.push({
          pathname: '/mfa/setup',
          state: { session, account }
        })
        return
      }

      if (status !== 'unverified') {
        dx.setIdentity(accountId, dashxToken)
        login({ accountId })
      }
    }
  }, [
    account,
    accountTokens,
    currentToken,
    session,
    history,
    login,
    sendMfaCode
  ])


  useEffect(() => {
    if (!account) {
      history.push('/login')
    }
  }, [account, history])

  const handleLogin = (values, ...rest) => {
    const loginParameters = {
      ...values,

      organizationId: account.organizationId,
      accountType: account.type
    }

    return createSession(loginParameters, ...rest)
  }

  const getRouteWithReferrer = route =>
    `${route}${referrer ? `?referrer=${referrer}` : ''}`

  return (
    <Fragment>
      <GATagConversionEvent />
      <AuthTitle
        data-test="organization-name"
        title={account && account.organizationDisplayName}
        subtitle={t('organizationLoginPage.subtitle')}
        to={getRouteWithReferrer('/choose-organization')}
        iconLeft="angle-left-thin"
      />
      <Row>
        <Column span={3} hiddenXs />
        <Column span={6}>
          <Card variant="secondary">
            {!isExternalRedirectLogin(account) && (
              <Tabs variant="fullWidth" inline>
                <Tab
                  active={account?.type === accountType.PROVIDER}
                  onClick={() => history.push(getRouteWithReferrer('/login'))}
                >
                  {t('organizationLoginPage.tabs.provider')}
                </Tab>
                <Tab
                  active={account?.type === accountType.STAFF}
                  onClick={() => history.push(getRouteWithReferrer('/login'))}
                >
                  {t('organizationLoginPage.tabs.staff')}
                </Tab>
              </Tabs>
            )}
            <OrganizationLoginForm
              account={account}
              onFormSubmit={handleLogin}
            />
          </Card>
        </Column>
      </Row>
    </Fragment>
  )
}

export default OrganizationLoginPage
